/**
 * Helper functions for automatic date input formatting
 * Handles various date input formats and converts them to DD/MM/YYYY format
 */

/**
 * Helper to detect if a column name indicates a date field
 * @param {string} columnName - The column name to check
 * @returns {boolean} Whether the column is likely a date field
 */
export const isDateField = (columnName) => {
  if (!columnName || typeof columnName !== 'string') return false;
  
  const dateKeywords = [
    'dob', 'date', 'birth', 'appointment', 'expiry', 'expire', 
    'created', 'updated', 'modified', 'scheduled', 'deadline',
    'start', 'end', 'begin', 'finish', 'due', 'valid', 'issued'
  ];
  
  const columnLower = columnName.toLowerCase();
  return dateKeywords.some(keyword => columnLower.includes(keyword));
};

/**
 * Helper to automatically format date input from various formats
 * Converts inputs like "12311999" or "123199" to "12/31/1999" (MM/DD/YYYY)
 * @param {string} value - The input value to format
 * @param {string} columnName - The column name (used to determine if formatting should be applied)
 * @returns {string} The formatted date string or original value if no formatting applied
 */
export const formatDateInput = (value, columnName) => {
  // Only apply formatting if it's a date field
  if (!isDateField(columnName)) {
    return value;
  }

  if (!value || typeof value !== 'string') {
    return value;
  }

  // Remove any existing separators and keep only digits
  const digitsOnly = value.replace(/[^\d]/g, '');
  
  // If we have exactly 8 digits, format as MM/DD/YYYY
  if (digitsOnly.length === 8) {
    const month = digitsOnly.substring(0, 2);
    const day = digitsOnly.substring(2, 4);
    const year = digitsOnly.substring(4, 8);
    
    // Basic validation for month and day ranges
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    
    if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
      return `${month}/${day}/${year}`;
    }
  }
  
  // If we have 6 digits, assume MMDDYY and convert to MM/DD/20YY or MM/DD/19YY
  if (digitsOnly.length === 6) {
    const month = digitsOnly.substring(0, 2);
    const day = digitsOnly.substring(2, 4);
    const year = digitsOnly.substring(4, 6);
    
    // Basic validation for month and day ranges
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    
    if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
      // Assume 20XX for years 00-30, 19XX for years 31-99
      const fullYear = parseInt(year, 10) <= 30 ? `20${year}` : `19${year}`;
      return `${month}/${day}/${fullYear}`;
    }
  }
  
  // Return original value if no formatting applied
  return value;
};

/**
 * Helper to validate if a date string is in DD/MM/YYYY format and is a valid date
 * @param {string} dateString - The date string to validate
 * @returns {boolean} Whether the date is valid
 */
export const isValidFormattedDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  
  // Check format DD/MM/YYYY
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
  if (!dateRegex.test(dateString)) return false;
  
  const [day, month, year] = dateString.split('/').map(Number);
  const dateObj = new Date(year, month - 1, day);
  
  return dateObj.getDate() === day &&
         dateObj.getMonth() === month - 1 &&
         dateObj.getFullYear() === year;
};

/**
 * Helper to clean a date string by removing any non-digit characters
 * @param {string} dateString - The date string to clean
 * @returns {string} Only the digits from the input
 */
export const cleanDateInput = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';
  return dateString.replace(/[^\d]/g, '');
};

/**
 * Helper to convert a formatted date (DD/MM/YYYY) to a Date object
 * @param {string} formattedDate - The formatted date string (DD/MM/YYYY)
 * @returns {Date|null} The Date object or null if invalid
 */
export const convertFormattedDateToDateObject = (formattedDate) => {
  if (!isValidFormattedDate(formattedDate)) return null;
  
  const [day, month, year] = formattedDate.split('/').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Helper to format a Date object to DD/MM/YYYY string
 * @param {Date} dateObj - The Date object to format
 * @returns {string} The formatted date string (DD/MM/YYYY)
 */
export const formatDateObjectToString = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Helper to get current date in DD/MM/YYYY format
 * @returns {string} Today's date in DD/MM/YYYY format
 */
export const getCurrentFormattedDate = () => {
  return formatDateObjectToString(new Date());
};

/**
 * Helper to check if a formatted date is in the past
 * @param {string} formattedDate - The formatted date string (DD/MM/YYYY)
 * @returns {boolean} Whether the date is in the past
 */
export const isDateInPast = (formattedDate) => {
  const dateObj = convertFormattedDateToDateObject(formattedDate);
  if (!dateObj) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  
  return dateObj < today;
};

/**
 * Helper to check if a formatted date is in the future
 * @param {string} formattedDate - The formatted date string (DD/MM/YYYY)
 * @returns {boolean} Whether the date is in the future
 */
export const isDateInFuture = (formattedDate) => {
  const dateObj = convertFormattedDateToDateObject(formattedDate);
  if (!dateObj) return false;
  
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set to end of day for accurate comparison
  
  return dateObj > today;
};
